import React from 'react';
import { GeoJsonLayer } from '@deck.gl/layers';
import { atom } from 'jotai';
import { useAtomValue, useStore } from 'jotai';
import { atomFamily } from 'jotai-family';
import type { FeatureCollection } from 'geojson';
import {
  TRIBAL_GEOMETRY_LADDER,
  useGeometryDetail,
  type GeometryDetailRequest,
} from '../geometryDetail';
import { fetchFlatGeobuf, type GeometryVariant } from '../../services/featuresApi';

export const viewTribalBoundariesAtom = atom<boolean>(false);

const EMPTY_COLLECTION: FeatureCollection = { type: 'FeatureCollection', features: [] };

const TRIBAL_TABLE = 'tribal_boundaries';

/**
 * The boundary set at one whole-table resolution. A static overlay, but a
 * multi-megabyte body to parse, and this hook mounts on every route, so a session
 * parses each resolution at most once. Keyed by resolution; viewport requests are
 * not held, since there is a box per camera position.
 *
 * An atom rather than a module-level promise, so the lifetime is the store's -- see
 * Incident/atoms.ts countyCollectionSelector for the same job.
 */
const tribalCollectionSelector = atomFamily((variant: GeometryVariant) =>
  atom(async () => fetchFlatGeobuf(TRIBAL_TABLE, { geometry: variant }))
);

/**
 * Boundaries at the resolution the camera needs, for the viewport only, or null
 * while the generalized whole-table set is faithful on its own. A superseded
 * request is aborted and what is drawn stays until the replacement resolves.
 *
 * The box is what makes the real geometry affordable -- 1.9 MB generalized against
 * 19 MB for the whole table -- and the API requires one for that resolution.
 */
function useTribalBoundaryDetail(
  request: GeometryDetailRequest | null,
  enabled: boolean
): FeatureCollection | null {
  const [detail, setDetail] = React.useState<FeatureCollection | null>(null);

  React.useEffect(() => {
    if (request == null || !enabled) {
      return;
    }

    const controller = new AbortController();
    fetchFlatGeobuf(TRIBAL_TABLE, {
      geometry: request.variant,
      bbox: request.bbox,
      signal: controller.signal,
    })
      .then(collection => {
        if (!controller.signal.aborted) setDetail(collection);
      })
      .catch((error: unknown) => {
        if (controller.signal.aborted) return;
        // The generalized boundaries stay on the map, so a failure costs fidelity
        // rather than the overlay.
        console.error('Failed to fetch viewport tribal boundaries:', error);
      });

    return () => {
      controller.abort();
    };
  }, [request, enabled]);

  // No viewport to refine: the last response is declined here rather than
  // cleared from the effect, which would cost a second render pass.
  return request == null ? null : detail;
}

export const useTribalLayer = () => {
  const viewTribalBoundaries = useAtomValue(viewTribalBoundariesAtom);
  const { base, detail: detailRequest } = useGeometryDetail(TRIBAL_GEOMETRY_LADDER);
  const [wholeTableData, setWholeTableData] = React.useState<FeatureCollection>(EMPTY_COLLECTION);
  const detailData = useTribalBoundaryDetail(detailRequest, viewTribalBoundaries);
  const store = useStore();

  React.useEffect(() => {
    // Nothing is fetched until the overlay is switched on. This hook is mounted
    // by the shared Map component on every route while the layer is drawn only
    // when the user asks for it, so an unconditional fetch spent 1.9 MB of
    // download and parse per session on boundaries most sessions never display.
    // A null resolution means the camera has not settled yet.
    if (!viewTribalBoundaries || base == null) {
      return;
    }

    let cancelled = false;
    // Resolves from the store when this resolution has already been fetched, and
    // the currently drawn boundaries stay on the map until the request resolves,
    // so a zoom change never leaves a gap.
    const collectionAtom = tribalCollectionSelector(base);
    store
      .get(collectionAtom)
      .then(data => {
        if (!cancelled) setWholeTableData(data);
      })
      .catch((error: unknown) => {
        // A rejection cached for the store's lifetime would answer every later
        // reader, so the atom holding it is dropped -- and only that one, since a
        // retry may already have minted its replacement.
        if (tribalCollectionSelector(base) === collectionAtom) {
          tribalCollectionSelector.remove(base);
        }
        console.error('Failed to fetch tribal boundaries:', error);
      });
    return () => {
      cancelled = true;
    };
  }, [base, viewTribalBoundaries, store]);

  // Boundaries the viewport set accounts for are dropped from the whole-table
  // set: they are the same rows at two resolutions, so drawing both would put two
  // outlines on every reservation on screen. objectid is the same value in both,
  // the table being static reference data.
  const tribalData = React.useMemo(() => {
    if (detailData == null) {
      return wholeTableData;
    }
    const detailed = new Set(detailData.features.map(feature => feature.properties?.objectid));
    return {
      type: 'FeatureCollection' as const,
      features: [
        ...wholeTableData.features.filter(feature => !detailed.has(feature.properties?.objectid)),
        ...detailData.features,
      ],
    };
  }, [wholeTableData, detailData]);

  const layer = React.useMemo(
    () =>
      new GeoJsonLayer({
        id: 'tribal-geojson-layer',
        data: tribalData,
        visible: viewTribalBoundaries,
        pickable: true,
        stroked: true,
        filled: true,
        lineWidthMinPixels: 2,
        getLineColor: [255, 204, 102, 120],
        getFillColor: [255, 204, 102, 10],
        getLineWidth: 4,

        updateTriggers: {
          getFillColor: [tribalData, viewTribalBoundaries],
          getLineColor: [tribalData, viewTribalBoundaries],
          getLineWidth: [tribalData, viewTribalBoundaries],
        },
      }),
    [tribalData, viewTribalBoundaries]
  );

  return layer;
};
